import { Check, CircleDot, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import { ORDEM_DO_CICLO, posicaoNoCiclo } from '@/features/releases/ciclo';
import {
  ROTULO_DE_SITUACAO,
  ROTULO_DE_TIPO_DE_RELEASE,
  type SituacaoDeRelease,
  type TipoDeRelease,
} from '@/features/releases/tipos';

/** As etiquetas e a régua do ciclo, compartilhadas pela lista e pela ficha. */

const ESTILO_DE_SITUACAO: Record<SituacaoDeRelease, string> = {
  PLANNING: 'bg-secondary text-secondary-foreground ring-border',
  BUILD: 'bg-primary/12 text-primary ring-primary/25',
  TESTING: 'bg-primary/12 text-primary ring-primary/25',
  READY: 'bg-success/12 text-success ring-success/25',
  DEPLOYING: 'bg-warning/15 text-warning ring-warning/30',
  DEPLOYED: 'bg-success/12 text-success ring-success/25',
  ROLLED_BACK: 'bg-destructive/12 text-destructive ring-destructive/25',
  CANCELLED: 'bg-muted text-muted-foreground ring-border line-through',
};

export function EtiquetaDeSituacao({ situacao }: { situacao: SituacaoDeRelease }) {
  return (
    <Etiqueta className={ESTILO_DE_SITUACAO[situacao]}>
      {situacao === 'ROLLED_BACK' ? <Undo2 className="mr-1 h-3 w-3" aria-hidden /> : null}
      {ROTULO_DE_SITUACAO[situacao]}
    </Etiqueta>
  );
}

export function EtiquetaDeTipoDeRelease({ tipo }: { tipo: TipoDeRelease }) {
  return (
    <Etiqueta
      className={
        tipo === 'EMERGENCY'
          ? 'bg-destructive/12 text-destructive ring-destructive/25'
          : 'bg-accent text-foreground/80 ring-border'
      }
    >
      {ROTULO_DE_TIPO_DE_RELEASE[tipo]}
    </Etiqueta>
  );
}

/**
 * A régua do ciclo.
 *
 * Revertida e cancelada saem da régua e viram uma faixa própria: desenhá-las
 * como o passo seguinte a "implantada" daria a impressão de progresso onde
 * houve exatamente o contrário.
 */
export function ReguaDoCiclo({ situacao }: { situacao: SituacaoDeRelease }) {
  const atual = posicaoNoCiclo(situacao);

  if (atual === -1) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold',
          situacao === 'ROLLED_BACK'
            ? 'border-destructive/30 bg-destructive/5 text-destructive'
            : 'border-border bg-muted/40 text-muted-foreground',
        )}
      >
        {situacao === 'ROLLED_BACK' ? (
          <Undo2 className="h-4 w-4" aria-hidden />
        ) : (
          <X className="h-4 w-4" aria-hidden />
        )}
        {ROTULO_DE_SITUACAO[situacao]} — fora do ciclo, e sem saída.
      </div>
    );
  }

  return (
    <ol className="flex flex-wrap items-center gap-1 text-xs">
      {ORDEM_DO_CICLO.map((etapa, indice) => {
        const passou = indice < atual;
        const aqui = indice === atual;
        return (
          <li key={etapa} className="flex items-center gap-1">
            <span
              aria-current={aqui ? 'step' : undefined}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
                aqui && 'bg-primary font-semibold text-primary-foreground',
                passou && 'bg-success/15 text-success',
                !aqui && !passou && 'bg-muted text-muted-foreground',
              )}
            >
              {passou ? <Check className="h-3 w-3" aria-hidden /> : null}
              {aqui ? <CircleDot className="h-3 w-3" aria-hidden /> : null}
              {ROTULO_DE_SITUACAO[etapa]}
            </span>
            {indice < ORDEM_DO_CICLO.length - 1 ? (
              <span aria-hidden className="text-muted-foreground">
                →
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
